/**
 * National Aged Care Quality Indicator Program — workforce indicator
 * (percentage of staff turnover), quarterly, residential care homes.
 *
 * Source: Department of Health, Disability and Ageing, "National Aged Care
 * Quality Indicator Program (QI Program) — Quick Reference Guide: Workforce"
 * (health.gov.au, 2025-10), and QI Program Manual Part A (September 2026)
 * s 18 Workforce. Verification note:
 * docs/operations/clause-verifications/2026-10-13-f4-doh-qi-workforce-indicator-primary-source-verification.md
 *
 * - Employed: worked at least 120 hours at the home in the previous quarter,
 *   any employment type (permanent, part-time, casual, contractor, agency).
 * - Stopped working: an employed staff member with a period of at least 60
 *   consecutive days in the current quarter without working at the home.
 * - Categories: service managers; nurse practitioners or registered nurses;
 *   enrolled nurses; personal care workers or nursing assistants.
 *
 * The report SQL (apps/web/src/lib/reports/qi-workforce-turnover-report.ts)
 * takes its thresholds from these constants; `qiWorkforceStaffOutcome` is the
 * reference implementation its behaviour is tested against.
 */

export const QI_WORKFORCE_EMPLOYED_MIN_HOURS = 120;
export const QI_WORKFORCE_STOPPED_MIN_CONSECUTIVE_DAYS = 60;

export const QI_WORKFORCE_CATEGORIES = ['SERVICE_MANAGER', 'RN_NP', 'EN', 'PCW_NA'] as const;
export type QiWorkforceCategory = (typeof QI_WORKFORCE_CATEGORIES)[number];

export const QI_WORKFORCE_CATEGORY_LABELS: Record<QiWorkforceCategory, string> = {
  SERVICE_MANAGER: 'Service managers',
  RN_NP: 'Nurse practitioners or registered nurses',
  EN: 'Enrolled nurses',
  PCW_NA: 'Personal care workers or nursing assistants',
};

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseIsoDate(value: string): { year: number; month: number; day: number } | null {
  const m = ISO_DATE.exec(value);
  if (!m) return null;
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
}

function isoDate(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Whole days from `from` to `to` (calendar dates, no timezone involved). */
function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/** True when start..end is exactly one calendar quarter (Jan-Mar, Apr-Jun, Jul-Sep, Oct-Dec). */
export function isCalendarQuarter(startDate: string, endDate: string): boolean {
  const start = parseIsoDate(startDate);
  if (!start || start.day !== 1 || ![1, 4, 7, 10].includes(start.month)) return false;
  const endMonth = start.month + 2;
  const lastDay = new Date(Date.UTC(start.year, endMonth, 0)).getUTCDate();
  return endDate === isoDate(start.year, endMonth, lastDay);
}

/** The calendar quarter before the quarter starting `quarterStart`. */
export function previousCalendarQuarter(quarterStart: string): { start: string; end: string } {
  const start = parseIsoDate(quarterStart);
  if (!start) throw new Error('invalid_quarter_start');
  const prevMonth = start.month === 1 ? 10 : start.month - 3;
  const prevYear = start.month === 1 ? start.year - 1 : start.year;
  const lastDay = new Date(Date.UTC(prevYear, prevMonth + 2, 0)).getUTCDate();
  return { start: isoDate(prevYear, prevMonth, 1), end: isoDate(prevYear, prevMonth + 2, lastDay) };
}

/**
 * Longest run of consecutive days in [quarterStart, quarterEnd] on which the
 * staff member did not work. No worked days means the whole quarter.
 */
export function longestNonWorkedRunDays(
  workedDates: readonly string[],
  quarterStart: string,
  quarterEnd: string,
): number {
  const days = [...new Set(workedDates)].filter((d) => d >= quarterStart && d <= quarterEnd).sort();
  if (days.length === 0) return daysBetween(quarterStart, quarterEnd) + 1;
  let longest = daysBetween(quarterStart, days[0]!);
  for (let i = 1; i < days.length; i += 1) {
    longest = Math.max(longest, daysBetween(days[i - 1]!, days[i]!) - 1);
  }
  return Math.max(longest, daysBetween(days[days.length - 1]!, quarterEnd));
}

/** Reference outcome for one staff member at one home. */
export function qiWorkforceStaffOutcome(input: {
  previousQuarterHours: number;
  currentQuarterWorkedDates: readonly string[];
  quarterStart: string;
  quarterEnd: string;
}): { employedAtStart: boolean; stoppedWorking: boolean } {
  const employedAtStart = input.previousQuarterHours >= QI_WORKFORCE_EMPLOYED_MIN_HOURS;
  const stoppedWorking =
    employedAtStart &&
    longestNonWorkedRunDays(
      input.currentQuarterWorkedDates,
      input.quarterStart,
      input.quarterEnd,
    ) >= QI_WORKFORCE_STOPPED_MIN_CONSECUTIVE_DAYS;
  return { employedAtStart, stoppedWorking };
}

/** Turnover % to one decimal place, or null when no one was employed. */
export function qiWorkforceTurnoverPct(
  stoppedWorking: number,
  employedAtStart: number,
): number | null {
  return employedAtStart > 0 ? Math.round((stoppedWorking / employedAtStart) * 1000) / 10 : null;
}
